import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePricing } from "@/lib/pricing";
import { BadgeCheck, CalendarClock, Loader2, Rocket, MapPin, Hourglass } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

export function PreLaunchBanner() {
  const final = !!usePricing()?.final_active;
  return (
    <div data-testid="pre-launch-banner"
         className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex items-start gap-3">
      <BadgeCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
      <div>
        <div className="text-sm font-semibold text-emerald-900" data-testid="pre-launch-banner-title">
          No payment required until launch
        </div>
        <div className="text-xs text-emerald-800 mt-0.5">
          Pick your plan now to reserve it. We won't charge your card until Cyannect officially launches.
          You'll get an email with a link to finish subscribing. {final ? "Until then you're on Basic (no monthly fee)." : "Until then you have Starter features."}
        </div>
      </div>
    </div>
  );
}

function ReservationSummary({ reservation, planName }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-700">
      <span data-testid="pre-launch-reserved-plan" className="font-display text-2xl text-slate-900">{planName}</span>
      <span data-testid="pre-launch-reserved-cadence">
        {reservation.cadence === "annual" ? "Billed annually" : "Billed monthly"}
      </span>
      <span className="inline-flex items-center gap-1" data-testid="pre-launch-reserved-provinces">
        <MapPin className="w-3.5 h-3.5 text-slate-400" /> {(reservation.provinces || []).join(", ") || "—"}
      </span>
      <span className="text-xs text-slate-500">Reserved {fmtDate(reservation.reserved_at)}</span>
    </div>
  );
}

export function ReservedPanel({ status, catalog, onChange, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const r = status.pre_launch_reservation;
  const plan = catalog.plans.find((p) => p.id === r.plan);
  const final = !!catalog.final_active;
  const cancel = async () => {
    try {
      setBusy(true);
      await api.delete("/billing/sg/reservation");
      toast.success("Reservation cancelled.");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel reservation.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5" data-testid="pre-launch-reserved-panel">
      <PreLaunchBanner />
      <div className="rounded-2xl border border-cyan-200 bg-white p-6">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Plan reserved</div>
        <ReservationSummary reservation={r} planName={plan ? plan.name : r.plan} />
        <p className="text-xs text-slate-500 mt-3">
          {final ? "You're on Basic (no monthly fee) until launch." : "You're on Starter features until launch."} Your reserved plan starts once you complete checkout on launch day.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="pre-launch-change-plan-btn" onClick={onChange}
                  className="pf-btn-primary px-5 py-2.5 rounded-full text-xs font-semibold">
            Change plan
          </button>
          <button data-testid="pre-launch-cancel-reservation-btn" onClick={cancel} disabled={busy}
                  className="text-xs font-semibold text-red-700 hover:text-red-900 underline disabled:opacity-50">
            {busy ? "Cancelling…" : "Cancel reservation"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CompleteReservationPanel({ status, catalog, onComplete, submitting }) {
  const r = status.pre_launch_reservation;
  const plan = catalog.plans.find((p) => p.id === r.plan);
  return (
    <div className="rounded-2xl border-2 border-cyan-400 bg-white p-6 space-y-4" data-testid="pre-launch-complete-panel">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-cyan-600 text-white">
        <Rocket className="w-3 h-3" /> Cyannect has launched
      </div>
      <div className="text-sm text-slate-700">Billing is now open. Finish subscribing to your reserved plan.</div>
      <ReservationSummary reservation={r} planName={plan ? plan.name : r.plan} />
      {status.pre_launch_grace_ends_at && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-2"
             data-testid="pre-launch-grace-notice">
          <Hourglass className="w-3.5 h-3.5" />
          {new Date(status.pre_launch_grace_ends_at) > new Date() ? (
            <>{catalog.final_active ? "Your reservation is held until" : "Starter access continues until"} <strong>{fmtDate(status.pre_launch_grace_ends_at)}</strong></>
          ) : (
            <>Grace period ended <strong>{fmtDate(status.pre_launch_grace_ends_at)}</strong>. Your reserved plan is still saved.</>
          )}
        </div>
      )}
      <div>
        <button data-testid="pre-launch-complete-btn" onClick={onComplete} disabled={submitting}
                className="pf-btn-primary px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
          Complete subscription
        </button>
      </div>
    </div>
  );
}
